import React, {useState, useEffect, useRef} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Formik, Form} from 'formik';
import Dialog from '@material-ui/core/Dialog';
import {FormControl} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import * as Yup from 'yup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import {
  DialogBody,
  DialogFields,
  DialogSubTitle,
  FieldInput,
  FieldLabel,
  DashboardField,
  DashboardFields,
  DialogFieldRemove
} from 'routes/App/dashboard/styles';
import {ButtonInfo, ButtonOutline, ButtonPrimary} from 'components/buttons';
import DialogContainer from 'components/DialogContainer/styles';
import DialogClose from 'components/DialogClose/styles';
import DialogTitle from 'components/DialogTitle/styles';
import CheckboxContainer from 'components/Checkbox/styles';
import SelectWrapper from 'components/select/SelectComponentStyles';
import {
  CLOSE_PRICE_ADJUSTMENT_MODAL,
  CREATE_PRICE_ADJUSTMENT,
  SET_PRICE_ADJUSTMENT_FORM_DATA,
  RESET_PRICE_ADJUSTMENT_FORM,
  DELETE_PRICE_ADJUSTMENT,
  RESET_ALL_PRICE_ADJUSTMENTS
} from 'store/platformPriceAdjustments/action';
import {PROVIDER_LABELS, PROVIDERS} from 'helpers/constants/storeSettings';
import {ErrorSign, ErrorSignWrapper} from 'components/errorSign/errorSign';
import {priceFormatter} from 'routes/App/menuEditor/constants';
import RadioS2E from 'components/Radiobox';
import {
  AdjustmentHeader,
  AdjustmentInfo,
  AdjustmentItem,
  AdjustmentsContainer,
  AdjustmentText,
  ReplaceAllAdjustmentsButton
} from 'routes/App/menuEditor/styles';
import ConfirmPriceAdjustModal from 'routes/App/menuEditor/Modals/ConfirmPriceAdjustModal';
import DialogActions from 'components/DialogActions/styles';
import DialogAction from 'components/DialogAction/styles';
import {
  getPlatformLabel,
  getScopeName,
  isPlatformAlreadyUsed
} from 'helpers/functionUtils/menuPriceAdjusment';

const validationSchema = Yup.object().shape({
  platform: Yup.string().required('Platform is required'),
  value: Yup.number()
    .required('Value is required')
    .positive('Value must be greater than 0'),
  hybridModeThreshold: Yup.number().when('hybridModeEnabled', {
    is: true,
    then: Yup.number()
      .required('Threshold is required')
      .positive('Threshold must be greater than 0'),
    otherwise: Yup.number()
  })
});

const GeneralPriceAdjustmentModal = () => {
  const dispatch = useDispatch();
  const isModalOpen = useSelector(
    (state) => state.platformPriceAdjustmentsReducer.isModalOpen
  );
  const formData = useSelector(
    (state) => state.platformPriceAdjustmentsReducer.formData
  );
  const isLoading = useSelector(
    (state) => state.platformPriceAdjustmentsReducer.isLoading
  );

  const allPriceAdjustments = useSelector(
    (state) => state.platformPriceAdjustmentsReducer.allPriceAdjustments
  );

  const {ExternalOrderProviders} = useSelector(
    (state) => state.user.activeLocation
  );

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [platformToDelete, setPlatformToDelete] = useState(null);

  const initialValues = {
    direction: formData.direction || 'increase',
    platform: formData.platform || '',
    hybridModeEnabled: formData.hybridModeEnabled || false,
    scope: formData.scope || 'items',
    type: formData.type || 'percentage',
    value: formData.value || '',
    hybridModeThreshold: formData.hybridModeThreshold || 5.0
  };

  const [previousPlatform, setPreviousPlatform] = useState('');
  const previousTypeRef = useRef(initialValues.type);

  useEffect(() => {
    if (previousTypeRef.current && previousTypeRef.current !== formData.type) {
      dispatch(SET_PRICE_ADJUSTMENT_FORM_DATA({value: ''}));
    }
    previousTypeRef.current = formData.type;
  }, [formData.type, dispatch]);

  const connectedPlatforms = [
    {value: PROVIDER_LABELS.UE, label: PROVIDER_LABELS.UE},
    {value: PROVIDER_LABELS.GH, label: PROVIDER_LABELS.GH},
    {value: PROVIDER_LABELS.DD, label: PROVIDER_LABELS.DD},
    {value: PROVIDER_LABELS.SL, label: PROVIDER_LABELS.SL}
  ];

  const handleClose = () => {
    dispatch(CLOSE_PRICE_ADJUSTMENT_MODAL());
    dispatch(RESET_PRICE_ADJUSTMENT_FORM());
  };

  const handlePlatformChange = (value, setFieldValue, currentPlatform) => {
    setPreviousPlatform(value);
    setFieldValue('platform', value);
    dispatch(SET_PRICE_ADJUSTMENT_FORM_DATA({platform: value}));
  };

  const confirmDelete = () => {
    if (platformToDelete) {
      dispatch(
        DELETE_PRICE_ADJUSTMENT({
          id: platformToDelete?._id,
          successCallback: () => {
            setDeleteConfirmOpen(false);
            setPlatformToDelete(null);
            dispatch(CLOSE_PRICE_ADJUSTMENT_MODAL());
          }
        })
      );
    }
  };

  const confirmResetAll = () => {
    dispatch(
      RESET_ALL_PRICE_ADJUSTMENTS({
        successCallback: () => {
          setResetConfirmOpen(false);
          dispatch(CLOSE_PRICE_ADJUSTMENT_MODAL());
        }
      })
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values, {setErrors, resetForm}) => {
        // Check if platform already has an adjustment
        if (isPlatformAlreadyUsed(values.platform, allPriceAdjustments)) {
          setErrors({
            platform:
              'Only one active price adjustment per platform is allowed.'
          });
          return;
        }

        const payload = {
          ProviderName: values.platform,
          PriceAdjustmentType:
            values.type === 'percentage' ? 'percentage' : 'fixed',
          PriceAdjustmentValue: parseFloat(values.value),
          HybridModeEnabled: values.hybridModeEnabled,
          PriceAdjustmentValueDirection: 'increase',
          PriceAdjustmentScope: values.scope
        };

        dispatch(
          CREATE_PRICE_ADJUSTMENT({
            payload,
            successCallback: () => {
              resetForm();
              dispatch(RESET_PRICE_ADJUSTMENT_FORM());
            }
          })
        );
      }}
    >
      {({values, handleChange, submitForm, setFieldValue, errors, touched}) => {
        // Check platform validation
        const platformError =
          isPlatformAlreadyUsed(values.platform, allPriceAdjustments) &&
          values.platform
            ? 'Only one active price adjustment per platform is allowed.'
            : errors.platform;

        const isFormValid =
          !isLoading &&
          values.platform &&
          values.value &&
          parseFloat(values.value) > 0;

        return (
          <Dialog onClose={handleClose} open={isModalOpen} scroll='body'>
            <DialogContainer className='DialogLg'>
              <DialogClose onClick={handleClose} />
              <DialogTitle>
                General Price <span>Adjustment</span>
              </DialogTitle>
              <DialogSubTitle className='textMedium'>
                Apply a price adjustment to all items on the selected platform.
              </DialogSubTitle>
              <Form>
                <ReplaceAllAdjustmentsButton>
                  <ButtonOutline
                    className='ButtonSmall'
                    type='button'
                    onClick={() => {
                      setResetConfirmOpen(true);
                    }}
                  >
                    <span>Reset to base price</span>
                  </ButtonOutline>
                </ReplaceAllAdjustmentsButton>
                <DialogBody>
                  <DialogFields className='w100'>
                    {/* Platform */}
                    <DashboardField className='w50'>
                      <FieldLabel>Platform</FieldLabel>
                      <SelectWrapper className='SelectMd w100'>
                        <FormControl className='select-formcontrol'>
                          <Select
                            value={values.platform}
                            name='platform'
                            onChange={(e) => {
                              handlePlatformChange(
                                e.target.value,
                                setFieldValue,
                                previousPlatform
                              );
                            }}
                            displayEmpty
                            className={
                              platformError && touched.platform ? 'error' : ''
                            }
                            MenuProps={{
                              disableScrollLock: true,
                              anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                              },
                              transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                              },
                              getContentAnchorEl: null
                            }}
                          >
                            <MenuItem value='' disabled>
                              Select
                            </MenuItem>
                            <MenuItem
                              key={PROVIDER_LABELS.UE}
                              value={PROVIDER_LABELS.UE}
                            >
                              {PROVIDER_LABELS.UE}
                            </MenuItem>
                            <MenuItem
                              key={PROVIDER_LABELS.GH}
                              value={PROVIDER_LABELS.GH}
                            >
                              {PROVIDER_LABELS.GH}
                            </MenuItem>
                            <MenuItem
                              key={PROVIDER_LABELS.DD}
                              value={PROVIDER_LABELS.DD}
                            >
                              {PROVIDER_LABELS.DD}
                            </MenuItem>
                            <MenuItem
                              key={PROVIDER_LABELS.SL}
                              value={PROVIDER_LABELS.SL}
                            >
                              {PROVIDER_LABELS.SL}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </SelectWrapper>
                      <ErrorSignWrapper>
                        {platformError && touched.platform && (
                          <ErrorSign>{platformError}</ErrorSign>
                        )}
                      </ErrorSignWrapper>
                    </DashboardField>

                    {/* Hybrid Mode */}
                    <DashboardField className='w33'>
                      <FieldLabel className='flex'>
                        <div className='text'>Hybrid Mode</div>
                        <ButtonInfo className='Outline ButtonInfoDrop MinusTopMargin5'>
                          <div className='DropInfo pointer-events-none DropInfoCenterMob Small'>
                            Apply only to items with base price &gt; $5
                          </div>
                        </ButtonInfo>
                      </FieldLabel>
                      <CheckboxContainer>
                        <FormControlLabel
                          className='Say2EatCheckbox'
                          control={
                            <Checkbox
                              labelStyle={{color: '#172751'}}
                              iconStyle={{fill: '#FF2D38'}}
                              inputStyle={{color: '#FF2D38'}}
                              style={{color: '#FF2D38'}}
                              checked={values.hybridModeEnabled}
                              onChange={(e) => {
                                setFieldValue(
                                  'hybridModeEnabled',
                                  e.target.checked
                                );
                                dispatch(
                                  SET_PRICE_ADJUSTMENT_FORM_DATA({
                                    hybridModeEnabled: e.target.checked
                                  })
                                );
                              }}
                            />
                          }
                          label='Enable'
                        />
                      </CheckboxContainer>
                    </DashboardField>
                  </DialogFields>

                  {/* Scope */}
                  <DashboardField className='w100 paddingL0 paddingR0 mBottom10'>
                    <FieldLabel>Scope</FieldLabel>
                    <SelectWrapper className='SelectMd w100'>
                      <FormControl className='select-formcontrol'>
                        <Select
                          value={values.scope}
                          name='scope'
                          onChange={(e) => {
                            setFieldValue('scope', e.target.value);
                            dispatch(
                              SET_PRICE_ADJUSTMENT_FORM_DATA({
                                scope: e.target.value
                              })
                            );
                          }}
                          MenuProps={{
                            disableScrollLock: true,
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left'
                            },
                            transformOrigin: {
                              vertical: 'top',
                              horizontal: 'left'
                            },
                            getContentAnchorEl: null
                          }}
                        >
                          <MenuItem value='items'>Items only</MenuItem>
                          <MenuItem value='itemsAndModifiers'>
                            Items and Modifiers
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </SelectWrapper>
                  </DashboardField>

                  <DashboardFields className='w100'>
                    {/* Type */}
                    <DashboardField className='w50 flexColumn AlignStart'>
                      <FieldLabel>Type</FieldLabel>
                      <RadioS2E
                        label='%'
                        type='radio'
                        name='type'
                        checked={values.type === 'percentage'}
                        onChange={() => {
                          const newType = 'percentage';
                          setFieldValue('type', newType);
                          setFieldValue('value', '');
                          dispatch(
                            SET_PRICE_ADJUSTMENT_FORM_DATA({
                              type: newType,
                              value: ''
                            })
                          );
                        }}
                      />
                      <RadioS2E
                        label='$'
                        type='radio'
                        name='type'
                        checked={values.type === 'fixed'}
                        onChange={() => {
                          const newType = 'fixed';
                          setFieldValue('type', newType);
                          setFieldValue('value', '');
                          dispatch(
                            SET_PRICE_ADJUSTMENT_FORM_DATA({
                              type: newType,
                              value: ''
                            })
                          );
                        }}
                      />
                    </DashboardField>

                    {/* Value */}
                    <DashboardField className='w50'>
                      <FieldLabel>Value</FieldLabel>
                      <div
                        style={{
                          position: 'relative',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        {values.type === 'fixed' && (
                          <span
                            style={{
                              position: 'absolute',
                              left: '17px',
                              fontSize: '14px',
                              color: '#2f3d48',
                              zIndex: 1
                            }}
                          >
                            $
                          </span>
                        )}
                        <FieldInput
                          name='value'
                          type='number'
                          value={values.value}
                          onChange={(e) => {
                            const value = priceFormatter(
                              e.target.value,
                              values.value
                            );
                            setFieldValue('value', value);
                            dispatch(SET_PRICE_ADJUSTMENT_FORM_DATA({value}));
                          }}
                          placeholder='e.g., 10'
                          className={
                            errors.value && touched.value ? 'error' : ''
                          }
                          style={
                            values.type === 'fixed'
                              ? {paddingLeft: '25px'}
                              : {paddingRight: '30px'}
                          }
                        />
                        {values.type === 'percentage' && (
                          <span
                            style={{
                              position: 'absolute',
                              right: '30px',
                              fontSize: '14px',
                              color: '#2f3d48',
                              zIndex: 1
                            }}
                          >
                            %
                          </span>
                        )}
                      </div>
                      <ErrorSignWrapper>
                        {errors.value && touched.value && (
                          <ErrorSign>{errors.value}</ErrorSign>
                        )}
                      </ErrorSignWrapper>
                    </DashboardField>
                  </DashboardFields>

                  {Array.isArray(allPriceAdjustments) &&
                    allPriceAdjustments.length > 0 && (
                      <AdjustmentsContainer>
                        <hr />
                        <DashboardFields className='dColumn paddingL10 paddingR10 marginTop'>
                          <AdjustmentHeader>
                            Active price adjustments
                          </AdjustmentHeader>
                          {allPriceAdjustments.map((adjustment) => (
                            <AdjustmentItem
                              key={adjustment._id || adjustment.ProviderName}
                            >
                              <AdjustmentInfo>
                                <AdjustmentText>
                                  {adjustment?.ProviderName}
                                </AdjustmentText>
                              </AdjustmentInfo>
                              <AdjustmentInfo>
                                <AdjustmentText>
                                  {getScopeName(
                                    adjustment?.PriceAdjustmentScope
                                  )}
                                </AdjustmentText>
                              </AdjustmentInfo>
                              <AdjustmentInfo>
                                <AdjustmentText>
                                  Hybrid:{' '}
                                  <span className='bold'>
                                    {' '}
                                    {adjustment?.HybridModeEnabled
                                      ? 'Yes'
                                      : 'No'}
                                  </span>
                                </AdjustmentText>
                              </AdjustmentInfo>
                              <AdjustmentInfo>
                                <AdjustmentText>
                                  +
                                  {adjustment?.PriceAdjustmentType ===
                                    'fixed' && '$'}
                                  {adjustment?.PriceAdjustmentValue}
                                  {adjustment?.PriceAdjustmentType ===
                                    'percentage' && ' %'}
                                </AdjustmentText>
                              </AdjustmentInfo>
                              <DialogFieldRemove
                                onClick={() => {
                                  setDeleteConfirmOpen(true);
                                  setPlatformToDelete(adjustment);
                                }}
                                title='Remove adjustment'
                                className='relative'
                              />
                            </AdjustmentItem>
                          ))}
                        </DashboardFields>
                      </AdjustmentsContainer>
                    )}

                  <DialogActions className='justifyCenter'>
                    <DialogAction className='w40'>
                      <ButtonOutline
                        onClick={handleClose}
                        className='marginRightSm'
                        type='button'
                      >
                        <span>Cancel</span>
                      </ButtonOutline>
                    </DialogAction>
                    <DialogAction className='w40'>
                      <ButtonPrimary
                        type='button'
                        onClick={submitForm}
                        disabled={!isFormValid}
                        style={{
                          opacity: !isFormValid ? 0.5 : 1,
                          cursor: !isFormValid ? 'default' : 'pointer'
                        }}
                      >
                        <span>Apply</span>
                      </ButtonPrimary>
                    </DialogAction>
                  </DialogActions>
                </DialogBody>
              </Form>
            </DialogContainer>
            <ConfirmPriceAdjustModal
              isOpen={deleteConfirmOpen}
              isLoading={isLoading}
              message={`This will remove all price overrides for ${
                platformToDelete?.ProviderName
                  ? getPlatformLabel(platformToDelete?.ProviderName)
                  : 'this platform'
              }.`}
              onCancel={() => {
                setDeleteConfirmOpen(false);
                setPlatformToDelete(null);
              }}
              onOk={confirmDelete}
            />
            <ConfirmPriceAdjustModal
              isOpen={resetConfirmOpen}
              isLoading={isLoading}
              message='Are you sure you want to delete price adjustment for all platforms?'
              subtitle='It will reset all general and custom items price adjustments to the Sauce base price.'
              onCancel={() => setResetConfirmOpen(false)}
              onOk={confirmResetAll}
            />
          </Dialog>
        );
      }}
    </Formik>
  );
};

export default GeneralPriceAdjustmentModal;
